<div class="vf-wai-overlay" *ngIf="open">

    <!-- Header -->
    <div class="vf-wai-header">
        <lucide-icon name="sparkles" [size]="16" [strokeWidth]="2" class="vf-wai-header-icon"></lucide-icon>
        <span class="vf-wai-header-title">Write with AI</span>
        <button type="button" class="vf-wai-icon-btn" title="Close" (click)="requestClose()">
            <lucide-icon name="x" [size]="16" [strokeWidth]="2"></lucide-icon>
        </button>
    </div>

    <!-- Editor area: skeleton while generating, tiptap editor when ready -->
    <div class="vf-wai-body">
        <!-- Skeleton bars only when we have no draft yet (initial generation).
             For refinement, we keep the existing draft visible and overlay a
             small "Generating" pill at the bottom instead. -->
        <ng-container *ngIf="state === 'generating' && !hasDraftContent">
            <div class="vf-wai-skeleton">
                <div class="vf-wai-skel-bar" style="width: 92%;"></div>
                <div class="vf-wai-skel-bar" style="width: 78%;"></div>
                <div class="vf-wai-skel-bar" style="width: 88%;"></div>
                <div class="vf-wai-skel-bar" style="width: 64%;"></div>
                <div class="vf-wai-skel-spacer"></div>
                <div class="vf-wai-skel-bar" style="width: 84%;"></div>
                <div class="vf-wai-skel-bar" style="width: 72%;"></div>
                <div class="vf-wai-skel-bar" style="width: 56%;"></div>
            </div>
        </ng-container>

        <ng-container *ngIf="state === 'error'">
            <div class="vf-wai-error">
                <lucide-icon name="x" [size]="20" [strokeWidth]="2"></lucide-icon>
                <div>
                    <div class="vf-wai-error-title">Something went wrong.</div>
                    <div class="vf-wai-error-message">{{ errorMessage }}</div>
                </div>
                <button type="button" class="vf-wai-cancel-btn" (click)="tryAgain()">Try again</button>
            </div>
        </ng-container>

        <div class="vf-wai-editor-host"
             [class.is-hidden]="state !== 'ready' && state !== 'streaming' && !(state === 'generating' && hasDraftContent)">
            <vf-tiptap-editor #draftEditor
                editorId="wai-overlay-draft"
                placeholder="AI draft will appear here…"
                height="100%"
                [value]="draftHtml"
                [maxLength]="20000"
                [showToolbar]="true"
                [showBubbleMenu]="true"
                [showProofread]="true"
                [showWriteAiToolbar]="false"
                [showSlashMenu]="false"
                [disableImageUpload]="true"
                (proofreadAction)="onOverlayProofread()"
                (valueChange)="onEditorValueChange($event)">
            </vf-tiptap-editor>
        </div>

        <!-- "Generating" pill — appears centered at the bottom of the editor
             area while AI is producing tokens (initial stream or refinement). -->
        <div class="vf-wai-generating-pill" *ngIf="isAiBusy">
            <span class="vf-wai-generating-pill-dot"></span>
            <span>Generating</span>
        </div>
    </div>

    <!-- Footer: prompt chip + redo + apply -->
    <div class="vf-wai-footer">
        <div class="vf-wai-footer-left">
            <span class="vf-wai-prompt-chip" *ngIf="seedPromptChipVisible && seedPrompt">
                <lucide-icon name="sparkles" [size]="12" [strokeWidth]="2"></lucide-icon>
                <span class="vf-wai-prompt-chip-text">{{ seedPrompt }}</span>
                <button type="button" class="vf-wai-prompt-chip-x" title="Dismiss" (click)="dismissPromptChip()">
                    <lucide-icon name="x" [size]="12" [strokeWidth]="2"></lucide-icon>
                </button>
            </span>
        </div>

        <div class="vf-wai-footer-right">
            <button type="button"
                    class="vf-wai-redo-btn"
                    title="Redo"
                    [disabled]="isAiBusy"
                    (click)="redo()">
                <lucide-icon name="repeat" [size]="14" [strokeWidth]="2"></lucide-icon>
                <span>Redo</span>
            </button>

            <div class="vf-wai-apply-wrap">
                <div class="vf-wai-apply-shell">
                    <button type="button"
                            class="vf-wai-apply-primary"
                            [disabled]="state !== 'ready'"
                            (click)="applyPrimary()">
                        {{ applyPrimaryLabel }}
                    </button>
                    <button type="button"
                            class="vf-wai-apply-caret"
                            *ngIf="showApplyCaret"
                            title="More options"
                            [disabled]="state !== 'ready'"
                            (click)="toggleApplyDropdown()">
                        <lucide-icon name="chevron-down" [size]="14" [strokeWidth]="2"></lucide-icon>
                    </button>
                </div>
                <div class="vf-wai-apply-menu" *ngIf="showApplyDropdown && hasNotesContent">
                    <button type="button"
                            class="vf-wai-apply-menu-item"
                            (click)="applyInsertAboveClicked()">
                        <lucide-icon name="pencil-line" [size]="14" [strokeWidth]="2" class="vf-wai-apply-menu-icon"></lucide-icon>
                        <span>Insert above notes</span>
                    </button>
                    <button type="button"
                            class="vf-wai-apply-menu-item"
                            (click)="applyReplaceClicked()">
                        <lucide-icon name="arrow-left-right" [size]="14" [strokeWidth]="2" class="vf-wai-apply-menu-icon"></lucide-icon>
                        <span>Replace notes</span>
                    </button>
                </div>
            </div>
        </div>
    </div>

    <!-- Refinement input -->
    <div class="vf-wai-refine">
        <div class="vf-wai-refine-shell">
            <textarea #refineInput
                      class="vf-wai-refine-input"
                      [(ngModel)]="refinePrompt"
                      [disabled]="isAiBusy"
                      placeholder="Refine or ask for changes…"
                      autosize
                      [minRows]="1"
                      [maxRows]="6"
                      (keydown)="onRefineKeydown($event)"></textarea>
            <button type="button"
                    class="vf-wai-refine-send"
                    title="Send"
                    [disabled]="!canSubmitRefine"
                    (click)="submitRefinement()">
                <lucide-icon name="send" [size]="14" [strokeWidth]="2"></lucide-icon>
            </button>
        </div>
        <p class="vf-wai-refine-hint">Enter to send · Shift+Enter for new line</p>
    </div>

    <!-- Discard confirmation -->
    <div class="vf-wai-discard-overlay" *ngIf="showDiscardConfirm" (mousedown)="cancelDiscard()">
        <div class="vf-wai-discard-dialog" (mousedown)="$event.stopPropagation()">
            <div class="vf-wai-discard-title">Discard AI draft?</div>
            <div class="vf-wai-discard-body">Closing now will lose the current draft.</div>
            <div class="vf-wai-discard-actions">
                <button type="button" class="vf-wai-cancel-btn" (click)="cancelDiscard()">Keep editing</button>
                <button type="button" class="vf-wai-discard-btn" (click)="confirmDiscard()">Discard</button>
            </div>
        </div>
    </div>

    <!-- Click-away catcher for the apply dropdown -->
    <div class="vf-wai-apply-clickaway" *ngIf="showApplyDropdown" (click)="closeApplyDropdown()"></div>
</div>
